import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { motion } from "framer-motion";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { MODULES, STATUS_COLORS } from "@/config/modules";
import ResourceForm from "@/components/ResourceForm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatINR, MONEY_FIELDS } from "@/lib/format";
import { sortByDate } from "@/lib/sortUtils";
import { ExportButton } from "@/components/ExportDialog";
import { MaterialWeightTile } from "@/components/MaterialWeightTile";
import { RateTrendChart } from "@/components/RateTrendChart";

const isDateCol = (c) => c.includes("date") || c === "created_at" || c === "updated_at";

function humanize(s) { return String(s).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()); }

const COLUMN_LABEL = { updated_at: "Last Updated", created_at: "Created", vendor_person: "Vendor", issuer: "Company", tax_invoice_number: "Tax Invoice No.", rate: "Sold Rate" };
function fmtDate(v) {
  if (!v) return v;
  const d = new Date(v);
  return isNaN(d.getTime()) ? v : d.toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "2-digit" });
}

function computeSummaries(module, items) {
  const total = (f, pred) => items.reduce((s, r) => (!pred || pred(r)) ? s + (parseFloat(r[f]) || 0) : s, 0);
  if (module.summaryType === "materials")
    return [
      { label: "Total Material Value", value: total("estimated_value") },
      { label: "Total Sales", value: total("estimated_value", (r) => ["Sold", "Dispatched"].includes(r.status)) },
      { label: "Pending / Unsold Value", value: total("estimated_value", (r) => !["Sold", "Dispatched"].includes(r.status)) },
    ];
  if (module.summaryType === "staff")
    return [{ label: "Total Monthly Payment (Active)", value: total("monthly_rate", (r) => r.status === "Active") }];
  if (module.summary)
    return [{ label: module.summary.label, value: total(module.summary.field) }];
  return [];
}

export default function ModulePage() {
  const { resource } = useParams();
  const module = MODULES[resource];
  const { user, can } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({});
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [viewerRow, setViewerRow] = useState(null);
  const dateCols = useMemo(() => (module ? module.columns.filter(isDateCol) : []), [module]);
  const [sort, setSort] = useState({ field: null, dir: "desc" });
  useEffect(() => { setSort({ field: dateCols[0] || null, dir: "desc" }); }, [resource]); // eslint-disable-line
  const toggleSort = (c) => setSort((s) => (s.field === c ? { field: c, dir: s.dir === "asc" ? "desc" : "asc" } : { field: c, dir: "desc" }));
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const displayItems = useMemo(() => {
    const df = module?.dateFilter;
    const inRange = df && (dateFrom || dateTo)
      ? items.filter((r) => { const d = (r[df] || "").slice(0, 10); return d && (!dateFrom || d >= dateFrom) && (!dateTo || d <= dateTo); })
      : items;
    return sort.field ? sortByDate(inRange, sort.field, sort.dir) : inRange;
  }, [items, sort, dateFrom, dateTo, module]);

  const load = useCallback(() => {
    if (!module) return;
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
    api.get(`/resource/${resource}`, { params })
      .then((r) => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
    if (module.summaryType === "materials" || resource === "rates") api.get(`/resource/${resource}`).then((r) => setAllItems(r.data)).catch(() => {});
  }, [resource, q, filters, module]);

  useEffect(() => { setQ(""); setFilters({}); setDateFrom(""); setDateTo(""); }, [resource]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  useEffect(() => {
    if (searchParams.get("add") === "1") {
      setEditing(null); setFormOpen(true);
      searchParams.delete("add"); setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  if (!module) return <div className="text-muted-foreground">Unknown module.</div>;
  if (!can(resource, "view"))
    return (
      <div className="max-w-md mx-auto mt-16 text-center" data-testid="restricted-notice">
        <div className="h-14 w-14 rounded-2xl bg-destructive/10 text-destructive flex items-center justify-center mx-auto mb-4">
          <Icons.Lock className="h-6 w-6" />
        </div>
        <h2 className="font-display text-xl font-bold">Access Restricted</h2>
        <p className="text-muted-foreground text-sm mt-2">You don't have permission to view {module.name}. Contact your administrator if you need access.</p>
      </div>
    );
  const Icon = Icons[module.icon] || Icons.Circle;
  const fileField = module.fields.find((f) => f.type === "file")?.name;
  const summaries = computeSummaries(module, items);

  const remove = async () => {
    try {
      await api.delete(`/resource/${resource}/${deleteId}`);
      toast.success(`${module.singular} deleted`);
      setDeleteId(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const download = async (format) => {
    try {
      const res = await api.get(`/export/${resource}`, { params: { format }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${resource}.${format === "excel" ? "xlsx" : format}`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };

  const telLink = (v) => `tel:${String(v).replace(/\s/g, "")}`;
  const waLink = (v) => `https://wa.me/${String(v).replace(/[^\d]/g, "")}`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${module.accent}1a`, color: module.accent }}>
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight">{module.name}</h1>
            <p className="text-sm text-muted-foreground">{displayItems.length} record{displayItems.length !== 1 ? "s" : ""}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {can(resource, "export") && resource === "expenses" && <ExportButton report="expenses" title="Expense Sheet" testid="export-btn" filters={{ ...filters, q }} />}
          {can(resource, "export") && resource !== "expenses" && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-1.5" data-testid="export-btn"><Icons.Download className="h-4 w-4" />Export</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => download("excel")} data-testid="export-excel">Excel (.xlsx)</DropdownMenuItem>
              <DropdownMenuItem onClick={() => download("pdf")} data-testid="export-pdf">PDF</DropdownMenuItem>
              <DropdownMenuItem onClick={() => download("csv")} data-testid="export-csv">CSV</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          )}
          {can(resource, "create") && (
          <Button className="gap-1.5 active:scale-95 transition-transform" onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="add-record-btn">
            <Icons.Plus className="h-4 w-4" />New {module.singular}
          </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${module.name.toLowerCase()}…`} className="pl-9" data-testid="module-search" />
        </div>
        {Object.entries(module.filters || {}).map(([field, opts]) => (
          <Select key={field} value={filters[field] || "all"} onValueChange={(v) => setFilters((p) => ({ ...p, [field]: v }))}>
            <SelectTrigger className="w-[170px]" data-testid={`filter-${field}`}><SelectValue placeholder={module.filterLabels?.[field] || humanize(field)} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All {module.filterLabels?.[field] || humanize(field)}</SelectItem>
              {opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        ))}
        {module.dateFilter && (
          <div className="flex items-center gap-1.5" data-testid="date-range-filter">
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-[150px]" data-testid="filter-date-from" />
            <span className="text-xs text-muted-foreground">to</span>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-[150px]" data-testid="filter-date-to" />
            {(dateFrom || dateTo) && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setDateFrom(""); setDateTo(""); }} data-testid="filter-date-clear"><Icons.X className="h-4 w-4" /></Button>}
          </div>
        )}
      </div>

      {module.summaryType === "materials" && <MaterialWeightTile items={allItems} />}
      {resource === "rates" && <RateTrendChart rates={allItems} />}

      {summaries.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-3">
          {summaries.map((s, i) => (
            <Card key={i} className="p-4 bg-primary/5 border-primary/20" data-testid={`summary-card-${i}`}>
              <div className="text-sm font-medium text-muted-foreground">{s.label}</div>
              <div className="font-display text-2xl font-bold" data-testid={`summary-total-${i}`}>{formatINR(s.value)}</div>
            </Card>
          ))}
        </div>
      )}

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/50 hover:bg-secondary/50">
                {module.columns.map((c) => isDateCol(c) ? (
                  <TableHead key={c} className="overline text-muted-foreground">
                    <button className="inline-flex items-center gap-1 hover:text-foreground" onClick={() => toggleSort(c)} data-testid={`sort-${c}`}>
                      {COLUMN_LABEL[c] || module.fields.find((f) => f.name === c)?.label || humanize(c)}
                      {sort.field === c ? (sort.dir === "asc" ? <Icons.ArrowUp className="h-3 w-3" /> : <Icons.ArrowDown className="h-3 w-3" />) : <Icons.ArrowUpDown className="h-3 w-3 opacity-40" />}
                    </button>
                  </TableHead>
                ) : <TableHead key={c} className="overline text-muted-foreground">{COLUMN_LABEL[c] || module.fields.find((f) => f.name === c)?.label || humanize(c)}</TableHead>)}
                <TableHead className="overline text-muted-foreground text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={module.columns.length + 1} className="text-center py-10 text-muted-foreground">Loading…</TableCell></TableRow>}
              {!loading && displayItems.length === 0 && (
                <TableRow><TableCell colSpan={module.columns.length + 1} className="text-center py-12">
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <Icon className="h-8 w-8 opacity-40" />
                    <p className="text-sm">No {module.name.toLowerCase()} yet.</p>
                    <Button size="sm" variant="secondary" onClick={() => { setEditing(null); setFormOpen(true); }}>Add the first one</Button>
                  </div>
                </TableCell></TableRow>
              )}
              {!loading && displayItems.map((row, i) => (
                <motion.tr key={row.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i * 0.03, 0.3) }}
                  className="border-b hover:bg-secondary/40 transition-colors">
                  {module.columns.map((c, ci) => {
                    const val = row[c];
                    let content;
                    if (["status", "priority", "rate_type"].includes(c) && val) {
                      content = <Badge variant="secondary" className={STATUS_COLORS[val] || ""}>{val}</Badge>;
                    } else if (MONEY_FIELDS.has(c) && val != null && val !== "" && !isNaN(parseFloat(val))) {
                      content = formatINR(val);
                    } else if (module.expiryFields?.includes(c) && val) {
                      const days = Math.round((new Date(val) - new Date(new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }))) / 86400000);
                      const cls = days < 0 ? "bg-red-100 text-red-800" : days <= 30 ? "bg-amber-100 text-amber-800" : "bg-emerald-50 text-emerald-800";
                      content = <span className={`inline-flex flex-col rounded-md px-2 py-0.5 text-xs ${cls}`} data-testid={`expiry-${c}-${row.id}`}><span>{fmtDate(val)}</span><span className="text-[10px] opacity-80">{days < 0 ? `Expired ${-days}d ago` : days === 0 ? "Due today" : `${days}d left`}</span></span>;
                    } else if ((c.includes("date") || c === "updated_at" || c === "created_at") && val) {
                      content = fmtDate(val);
                    } else if (Array.isArray(val)) {
                      content = val.join(", ");
                    } else {
                      content = val ?? <span className="text-muted-foreground">—</span>;
                    }
                    const clickable = ci === 0 && fileField && row[fileField]?.storage_path;
                    return (
                      <TableCell key={c} className={ci === 0 ? "font-medium" : ""}>
                        {clickable ? (
                          <button onClick={() => setViewerRow(row)} data-testid={`view-${row.id}`}
                            className="text-primary hover:underline inline-flex items-center gap-1.5 text-left">
                            <Icons.Eye className="h-3.5 w-3.5 shrink-0" />{content}
                          </button>
                        ) : content}
                      </TableCell>
                    );
                  })}
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {module.quickActions && (row.mobile || row.whatsapp || row.email) && (
                        <>
                          {row.mobile && <a href={telLink(row.mobile)} className="p-1.5 rounded hover:bg-secondary text-emerald-600" data-testid={`call-${row.id}`}><Icons.Phone className="h-4 w-4" /></a>}
                          {(row.whatsapp || row.mobile) && <a href={waLink(row.whatsapp || row.mobile)} target="_blank" rel="noreferrer" className="p-1.5 rounded hover:bg-secondary text-green-600" data-testid={`wa-${row.id}`}><Icons.MessageCircle className="h-4 w-4" /></a>}
                          {row.email && <a href={`mailto:${row.email}`} className="p-1.5 rounded hover:bg-secondary text-blue-600" data-testid={`mail-${row.id}`}><Icons.Mail className="h-4 w-4" /></a>}
                        </>
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`row-menu-${row.id}`}><Icons.MoreVertical className="h-4 w-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {can(resource, "edit") && <DropdownMenuItem onClick={() => { setEditing(row); setFormOpen(true); }} data-testid={`edit-${row.id}`}><Icons.Pencil className="h-4 w-4 mr-2" />Edit</DropdownMenuItem>}
                          {can(resource, "delete") && <DropdownMenuItem className="text-destructive" onClick={() => setDeleteId(row.id)} data-testid={`delete-${row.id}`}><Icons.Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>}
                          {!can(resource, "edit") && !can(resource, "delete") && <DropdownMenuItem disabled>No actions</DropdownMenuItem>}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </motion.tr>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <ResourceForm open={formOpen} onOpenChange={setFormOpen} module={module} record={editing} onSaved={load} />

      <Dialog open={!!viewerRow} onOpenChange={(o) => !o && setViewerRow(null)}>
        <DialogContent className="max-w-4xl w-[95vw]" data-testid="doc-viewer">
          <DialogHeader>
            <DialogTitle className="font-display truncate">{viewerRow?.[module.titleField] || "Preview"}</DialogTitle>
            <DialogDescription className="truncate">{viewerRow?.[fileField]?.original_filename}</DialogDescription>
          </DialogHeader>
          {(() => {
            const f = viewerRow?.[fileField];
            if (!f?.storage_path) return null;
            const url = fileUrl(f.storage_path);
            const ct = f.content_type || "";
            if (ct.includes("pdf"))
              return <iframe src={url} title="preview" className="w-full h-[70vh] rounded-md border" />;
            if (ct.startsWith("image"))
              return <img src={url} alt="preview" className="max-h-[70vh] mx-auto rounded-md" />;
            return (
              <div className="py-12 text-center text-muted-foreground">
                <Icons.FileText className="h-10 w-10 mx-auto mb-3 opacity-40" />
                <p className="text-sm">Preview isn't available for this file type.</p>
              </div>
            );
          })()}
          {viewerRow?.[fileField]?.storage_path && (
            <a href={fileUrl(viewerRow[fileField].storage_path)} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline" data-testid="doc-download-link">
              <Icons.Download className="h-4 w-4" />Open in new tab / Download
            </a>
          )}
        </DialogContent>
      </Dialog>


      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {module.singular}?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone. The record will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
